'use client'

import * as React from 'react'
import { LockClosedIcon } from '@heroicons/react/24/outline'
import { useLocation, useNavigate } from 'react-router-dom'
import { Crown } from 'iconoir-react'
import { Tooltip } from 'app/components/Tooltip'
import { cn } from 'app/lib/utils'
import { Tabs, TabsList, TabsTrigger } from 'app/components/ui/tabs'
import type { TabType } from 'app/components/Tabs'

export type { TabType }

type NavTabsViewProps = {
  tabs: TabType[]
  className?: string
  activeHref?: string
  search?: string
}

export function NavTabsView({ tabs, className, activeHref, search = `` }: NavTabsViewProps) {
  const navigate = useNavigate()

  return (
    <Tabs value={activeHref} onValueChange={(value) => navigate(value + search)} className={cn(`w-full`, className)}>
      <TabsList
        variant="line"
        className="h-auto w-full justify-start gap-x-1 overflow-x-auto overflow-y-hidden rounded-none border-b border-default bg-transparent p-0"
      >
        {tabs.map((tab) => (
          <Tooltip
            key={tab.name}
            message={tab.disabled && tab.disabledMessage}
            className={tab.disabled ? `cursor-not-allowed` : undefined}
          >
            <TabsTrigger value={tab.href} disabled={tab.disabled}>
              {tab.name}
              {tab.disabled && <LockClosedIcon className="ml-1 h-4 w-4" />}
              {tab.superUserOnly && (
                <div className="size-5 flex items-center justify-center rounded bg-amber-500/[0.08]">
                  <Crown width={12} height={12} className="text-amber-800" />
                </div>
              )}
              {tab.totalItems && (
                <span
                  className={cn(
                    `min-w-5 ml-2 rounded-3xl px-1 py-0.5 text-center text-caption-sm`,
                    activeHref === tab.href ? `bg-blue-600 text-on-color` : `bg-surface-strong text-default`,
                  )}
                >
                  {tab.totalItems}
                </span>
              )}
            </TabsTrigger>
          </Tooltip>
        ))}
      </TabsList>
    </Tabs>
  )
}

type NavTabsProps = {
  tabs: TabType[]
  className?: string
}

export function NavTabs({ tabs, className }: NavTabsProps) {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()

  const activeTab =
    tabs.find((tab) => (tab.href.startsWith(`/`) ? pathname.startsWith(tab.href) : pathname.endsWith(`/${tab.href}`)))
      ?.href ?? ``

  return (
    <Tabs value={activeTab} onValueChange={(value) => navigate(value + search)} className={cn(`w-full`, className)}>
      <TabsList
        variant="line"
        className="h-auto w-full justify-start gap-x-1 overflow-x-auto overflow-y-hidden rounded-none border-b border-default bg-transparent p-0"
      >
        {tabs.map((tab) => (
          <Tooltip
            key={tab.name}
            message={tab.disabled && tab.disabledMessage}
            className={tab.disabled ? `cursor-not-allowed` : undefined}
          >
            <TabsTrigger
              value={tab.href}
              disabled={tab.disabled}
              className={cn(
                `relative inline-flex shrink-0 items-center gap-1.5 rounded-none px-2 pb-3 pt-0.5 text-ui-sm font-medium whitespace-nowrap shadow-none transition-colors`,
                tab.disabled ? `text-minimal pointer-events-none` : `text-subtle hover:text-default`,
                `data-[state=active]:text-blue-900 data-[state=active]:shadow-none`,
                `data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-0 data-[state=active]:after:right-0 data-[state=active]:after:h-0.5 data-[state=active]:after:bg-blue-600`,
              )}
            >
              {tab.name}
              {tab.disabled && <LockClosedIcon className="ml-1 h-4 w-4" />}
              {tab.superUserOnly && (
                <div className="size-5 flex items-center justify-center rounded bg-amber-500/[0.08]">
                  <Crown width={12} height={12} className="text-amber-800" />
                </div>
              )}
              {tab.totalItems && (
                <span
                  className={cn(
                    `min-w-5 ml-2 rounded-3xl px-1 py-0.5 text-center text-caption-sm`,
                    activeTab === tab.href ? `bg-blue-600 text-on-color` : `bg-surface-strong text-default`,
                  )}
                >
                  {tab.totalItems}
                </span>
              )}
            </TabsTrigger>
          </Tooltip>
        ))}
      </TabsList>
    </Tabs>
  )
}
